import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { useLingui } from "@lingui/react";
import { LayoutGrid, LayoutList, Search } from "lucide-react";

export type DataToolbarViewMode = "table" | "item";

interface DataToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  viewMode: DataToolbarViewMode;
  onViewModeChange: (mode: DataToolbarViewMode) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  showViewToggle?: boolean;
  children?: ReactNode;
}

export function DataToolbar({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  searchPlaceholder,
  searchAriaLabel,
  showViewToggle = true,
  children,
}: DataToolbarProps) {
  const { i18n } = useLingui();

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="relative w-75 max-w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
        <Input
          placeholder={searchPlaceholder ?? i18n._("Search…")}
          aria-label={searchAriaLabel}
          className="pl-9 h-11 text-base border-(--shell-border) shadow-xs rounded-lg"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="flex items-center gap-2">
        {children}

        {showViewToggle && (
          <div
            className="flex items-center bg-(--color-surface-muted) rounded-lg p-1 h-11"
            role="group"
            aria-label={i18n._("View mode")}
          >
            <button
              type="button"
              onClick={() => onViewModeChange("table")}
              aria-label={i18n._("Table view")}
              aria-pressed={viewMode === "table"}
              className={`size-9 flex items-center justify-center rounded-md ${viewMode === "table" ? "bg-(--color-surface-content)" : ""}`}
            >
              <LayoutList
                className={`size-4 ${viewMode === "table" ? "text-(--color-text-primary)" : "text-(--color-text-secondary)"}`}
              />
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("item")}
              aria-label={i18n._("Item view")}
              aria-pressed={viewMode === "item"}
              className={`size-9 flex items-center justify-center rounded-md ${viewMode === "item" ? "bg-(--color-surface-content)" : ""}`}
            >
              <LayoutGrid
                className={`size-4 ${viewMode === "item" ? "text-(--color-text-primary)" : "text-(--color-text-secondary)"}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
